import { useEffect, useState } from 'react'
import Layout from '../components/Layout'
import { api, embedUrl, fmtMonth, fileSize, type PortfolioItem } from '../lib/api'
import { useUploads } from '../lib/uploads'

const input = 'w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm focus:border-orange outline-none'
const label = 'block text-[11px] uppercase tracking-widest text-acier mb-1'
const btn = 'px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors'
const empty = (): Partial<PortfolioItem> => ({ title: '', company: '', description: '', project_date: '', video_url: '', thumbnail_url: '', published: true })

function Thumb({ item }: { item: Partial<PortfolioItem> }) {
  const yt = (item.video_url || '').match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/)
  const src = item.thumbnail_url || (yt ? `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg` : '')
  return (
    <div className="w-32 aspect-video bg-anthracite border border-white/10 overflow-hidden shrink-0 rounded-sm">
      {src ? <img src={src} className="w-full h-full object-cover" alt="" />
        : item.video_url && !embedUrl(item.video_url) ? <video src={item.video_url + '#t=0.5'} preload="metadata" muted className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-acier text-xs">—</div>}
    </div>
  )
}

export default function Portfolio() {
  const [items, setItems] = useState<PortfolioItem[]>([])
  const { start } = useUploads()
  const [edit, setEdit] = useState<Partial<PortfolioItem> | null>(null)
  const [msg, setMsg] = useState('')
  const [pct, setPct] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const load = () => api<{ items: PortfolioItem[] }>('/portfolio?all=1').then(j => setItems(j.items)).catch(e => setMsg('Erreur : ' + e.message))
  useEffect(() => { load() }, [])

  async function save() {
    if (!edit?.title?.trim()) { setMsg('Le titre est requis.'); return }
    setBusy(true); setMsg('')
    try { await api('/portfolio', { method: 'POST', json: edit }); setEdit(null); await load(); setMsg('✅ Projet enregistré.') }
    catch (e: any) { setMsg('Erreur : ' + e.message) } finally { setBusy(false) }
  }
  async function remove(item: PortfolioItem) {
    if (!confirm(`Supprimer « ${item.title} » ? La vidéo téléversée sera effacée aussi.`)) return
    try { await api(`/portfolio?id=${item.id}`, { method: 'DELETE' }); await load(); setMsg('Projet supprimé.') } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  async function togglePublished(item: PortfolioItem) {
    try { await api('/portfolio', { method: 'POST', json: { ...item, published: !item.published } }); await load() } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  async function move(idx: number, dir: -1 | 1) {
    const ids = items.map(i => i.id); const j = idx + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[idx], ids[j]] = [ids[j], ids[idx]]
    try { const r = await api<{ items: PortfolioItem[] }>('/portfolio/reorder', { method: 'POST', json: { ids } }); setItems(r.items) } catch (e: any) { setMsg('Erreur : ' + e.message) }
  }
  async function uploadMedia(e: React.ChangeEvent<HTMLInputElement>, field: 'video_url' | 'thumbnail_url') {
    const f = e.target.files?.[0]; if (!f || !edit) return
    setPct(0); setMsg(`Téléversement de ${f.name} (${fileSize(f.size)})…`)
    try {
      const r = await start('/portfolio-media', [f])
      setEdit({ ...edit, [field]: r.files[0].url }); setMsg('✅ Fichier téléversé. Pensez à enregistrer.')
    } catch (err: any) { setMsg('Erreur : ' + err.message) } finally { setPct(null); e.target.value = '' }
  }

  return (
    <Layout title="Portfolio">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <a href="/admin" className="text-xs uppercase tracking-widest text-acier hover:text-orange">← Admin</a>
        <button onClick={() => setEdit(empty())} className={`${btn} bg-orange hover:bg-orange-hover`}>+ Ajouter un projet</button>
      </div>
      {msg && <p className="text-xs text-acier mt-4">{msg}</p>}

      {edit && (
        <div className="mt-6 border border-orange/40 rounded p-5 bg-white/[0.02] space-y-4">
          <h2 className="font-display text-2xl tracking-wide">{edit.id ? 'Modifier le projet' : 'Nouveau projet'}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={label}>Titre *</label><input className={input} value={edit.title || ''} onChange={e => setEdit({ ...edit, title: e.target.value })} placeholder="Ex. : Vidéo corporative, usine de Lévis" /></div>
            <div><label className={label}>Entreprise / client</label><input className={input} value={edit.company || ''} onChange={e => setEdit({ ...edit, company: e.target.value })} placeholder="Nom de l'entreprise" /></div>
            <div><label className={label}>Date du projet</label><input type="date" className={input} value={edit.project_date || ''} onChange={e => setEdit({ ...edit, project_date: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className={label}>Description</label><textarea className={input + ' min-h-[100px]'} value={edit.description || ''} onChange={e => setEdit({ ...edit, description: e.target.value })} placeholder="Contexte, mandat, résultat…" /></div>

            <div className="sm:col-span-2 border-t border-white/10 pt-4">
              <label className={label}>Vidéo</label>
              <div className="flex flex-wrap gap-3 items-center">
                <label className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer`}>
                  {pct !== null ? 'Envoi en cours…' : '⬆ Téléverser un fichier vidéo'}
                  <input type="file" accept="video/*" className="hidden" disabled={pct !== null} onChange={e => uploadMedia(e, 'video_url')} />
                </label>
                <span className="text-acier text-xs">ou coller un lien YouTube / Vimeo / mp4 :</span>
              </div>
              <input className={input + ' mt-2'} value={edit.video_url || ''} onChange={e => setEdit({ ...edit, video_url: e.target.value })} placeholder="https://youtu.be/… ou /papi/media/…" />
              {pct !== null && <p className="text-xs text-acier mt-2">Progression dans le panneau en bas à droite.</p>}
            </div>

            <div className="sm:col-span-2">
              <label className={label}>Vignette (optionnel, sinon 1re image de la vidéo ou vignette YouTube)</label>
              <div className="flex flex-wrap gap-3 items-center">
                <label className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer`}>
                  ⬆ Téléverser une image
                  <input type="file" accept="image/*" className="hidden" disabled={pct !== null} onChange={e => uploadMedia(e, 'thumbnail_url')} />
                </label>
                <input className={input + ' flex-1 min-w-[200px]'} value={edit.thumbnail_url || ''} onChange={e => setEdit({ ...edit, thumbnail_url: e.target.value })} placeholder="URL d'image (optionnel)" />
                {edit.thumbnail_url && <button onClick={() => setEdit({ ...edit, thumbnail_url: '' })} className="text-xs text-acier hover:text-red-300">Retirer</button>}
              </div>
            </div>

            <div className="sm:col-span-2 flex items-center gap-6">
              <Thumb item={edit} />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={edit.published !== false} onChange={e => setEdit({ ...edit, published: e.target.checked })} className="accent-orange" />
                Visible sur le site
              </label>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={busy || pct !== null} className={`${btn} bg-orange hover:bg-orange-hover disabled:opacity-50`}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
            <button onClick={() => setEdit(null)} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10`}>Annuler</button>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-3">
        {items.length === 0 && !edit && <p className="text-acier text-sm">Aucun projet pour l'instant. Ajoutez votre première réalisation.</p>}
        {items.map((item, idx) => (
          <div key={item.id} className={`flex flex-wrap sm:flex-nowrap items-center gap-4 border rounded px-4 py-3 ${item.published ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
            <div className="flex flex-col gap-1 text-acier">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="hover:text-orange disabled:opacity-20 leading-none" title="Monter">▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="hover:text-orange disabled:opacity-20 leading-none" title="Descendre">▼</button>
            </div>
            <Thumb item={item} />
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium">{item.title}</div>
              <div className="text-xs text-orange uppercase tracking-widest mt-0.5">{[item.company, fmtMonth(item.project_date)].filter(Boolean).join(' · ')}</div>
              {item.description && <div className="text-xs text-acier mt-1 line-clamp-2">{item.description}</div>}
              {!item.video_url && <div className="text-xs text-yellow-300 mt-1">⚠ Aucune vidéo</div>}
            </div>
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest whitespace-nowrap">
              <button onClick={() => togglePublished(item)} className={item.published ? 'text-green-300 hover:text-yellow-300' : 'text-acier hover:text-green-300'}>{item.published ? 'Visible' : 'Masqué'}</button>
              <button onClick={() => { setEdit({ ...item, project_date: item.project_date || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }) }} className="text-orange hover:underline">Modifier</button>
              <button onClick={() => remove(item)} className="text-acier hover:text-red-300">Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  )
}
